import type { Download } from "./client";
import type { Helpers } from "./endpoints-shared";
import type { In, Out } from "./operations";
import type { PageRequest } from "./types";

/** Register operations use the same server-authoritative totals and idempotent ids as the terminals. */
export function posCheckEndpoints({
  http,
  get,
  post,
  patch,
  paged,
}: Pick<Helpers, "http" | "get" | "post" | "patch" | "paged" | "put" | "del">) {
  return {
    /** Read-only standard-bill shares; does not authorize payment, stock movement or settlement. */
    previewEvenPayments: (id: string, body: In<"pos_preview_even_payments">) =>
      post<Out<"pos_preview_even_payments">>(`/pos/checks/${id}/even-payment-preview`, body),
    previewCheckMove: (id: string, body: In<"pos_preview_check_move">) =>
      post<Out<"pos_preview_check_move">>(`/pos/checks/${id}/move-preview`, body),
    moveCheckLines: (id: string, body: In<"pos_move_check_lines">) =>
      post<Out<"pos_move_check_lines">>(`/pos/checks/${id}/move`, body),
    listServers: (page: PageRequest & { q?: string | undefined } = {}) =>
      get<Out<"pos_servers">>("/pos/servers", paged(page, { q: page.q })),
    listChecks: (
      page: PageRequest & {
        outletId?: string | undefined;
        businessDate?: string | undefined;
        status?: "open" | "settled" | "voided" | "merged" | undefined;
        mine?: boolean | undefined;
        q?: string | undefined;
      } = {},
    ) =>
      get<Out<"pos_list_checks">>(
        "/pos/checks",
        paged(page, {
          outlet_id: page.outletId,
          business_date: page.businessDate,
          status: page.status,
          mine: page.mine,
          q: page.q,
        }),
      ),
    getCheck: (id: string, signal?: AbortSignal) =>
      get<Out<"pos_get_check">>(`/pos/checks/${id}`, { signal }),
    openCheck: (body: In<"pos_open_check">) =>
      post<Out<"pos_open_check">>("/pos/checks", body),
    updateCheck: (id: string, body: In<"pos_update_check">) =>
      patch<Out<"pos_update_check">>(`/pos/checks/${id}`, body),
    addLines: (id: string, body: In<"pos_add_lines">) =>
      post<Out<"pos_add_lines">>(`/pos/checks/${id}/lines`, body),
    updateLine: (id: string, lineId: string, body: In<"pos_update_line">) =>
      patch<Out<"pos_update_line">>(`/pos/checks/${id}/lines/${lineId}`, body),
    sendCheck: (id: string, body?: In<"pos_send_check">) =>
      post<Out<"pos_send_check">>(`/pos/checks/${id}/send`, body),
    voidLine: (id: string, lineId: string, body?: In<"pos_void_line">) =>
      post<Out<"pos_void_line">>(
        `/pos/checks/${id}/lines/${lineId}/void`,
        body,
      ),
    compLine: (id: string, lineId: string, body: In<"pos_comp_line">) =>
      post<Out<"pos_comp_line">>(
        `/pos/checks/${id}/lines/${lineId}/comp`,
        body,
      ),
    voidCheck: (id: string, body: In<"pos_void_check">) =>
      post<Out<"pos_void_check">>(`/pos/checks/${id}/void`, body),
    grantApproval: (body: In<"pos_grant_approval">) =>
      post<Out<"pos_grant_approval">>("/pos/approvals", body),
    settleCheck: (id: string, body: In<"pos_settle_check">) =>
      post<Out<"pos_settle_check">>(`/pos/checks/${id}/settle`, body),
    receipt: (id: string, signal?: AbortSignal) =>
      get<Out<"pos_get_receipt">>(`/pos/checks/${id}/receipt`, { signal }),
    findMembers: (q: string, signal?: AbortSignal) =>
      get<Out<"pos_find_members">>("/pos/members", { query: { q }, signal }),
    getMemberCard: (id: string, signal?: AbortSignal) =>
      get<Out<"pos_get_member_card">>(`/pos/members/${id}`, { signal }),
    downloadMemberPhoto: (
      id: string,
      size: "full" | "thumbnail" = "thumbnail",
      signal?: AbortSignal,
    ) =>
      http.request<Download>(
        "GET",
        `/pos/members/${id}/photo${size === "thumbnail" ? "/thumbnail" : ""}`,
        { responseType: "blob", signal },
      ),
    listReasons: (
      options: {
        kind?: "void" | "comp" | undefined;
        active?: boolean | undefined;
      } = {},
      signal?: AbortSignal,
    ) =>
      get<Out<"pos_list_reasons">>("/pos/reasons", { query: options, signal }),
    createReason: (body: In<"pos_create_reason">) =>
      post<Out<"pos_create_reason">>("/pos/reasons", body),
    updateReason: (id: string, body: In<"pos_update_reason">) =>
      patch<Out<"pos_update_reason">>(`/pos/reasons/${id}`, body),
    previewDayClose: (id: string, businessDate: string, signal?: AbortSignal) =>
      get<Out<"pos_preview_day_close">>(`/pos/outlets/${id}/day-close`, {
        query: { business_date: businessDate },
        signal,
      }),
    closeDay: (id: string, body: In<"pos_close_day">) =>
      post<Out<"pos_close_day">>(`/pos/outlets/${id}/day-close`, body),
    listDayCloses: (
      page: PageRequest & { outletId?: string | undefined } = {},
    ) =>
      get<Out<"pos_list_day_closes">>(
        "/pos/day-closes",
        paged(page, { outlet_id: page.outletId }),
      ),
  };
}
